import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import getAuthStatusByEmail from "../services/getAuthStatusByEmail";
import {
  clearAuthSession,
  getStoredUserEmail,
  setStoredUserEmail,
} from "../utils/authStorage";
import {
  clearInMemoryAccessToken,
  getInMemoryAccessToken,
  setInMemoryAccessToken,
} from "../../../api/client";

const AuthContext = createContext(null);

function normalizeAuthUser(user = {}) {
  return {
    id: user?.user_id || user?.id || "",
    user_id: user?.user_id || user?.id || "",
    name: user?.name || user?.full_name || "",
    full_name: user?.full_name || user?.name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    profile_status: user?.profile_status || "",
    nafath_status: user?.nafath_status || "",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isHydratingAuth, setIsHydratingAuth] = useState(true);

  const clearAuthenticatedSession = useCallback(() => {
    clearInMemoryAccessToken();
    clearAuthSession();
    setUser(null);
  }, []);

  const setAuthenticatedSession = useCallback(({ email, token, user: apiUser }) => {
    const normalizedUser = normalizeAuthUser(apiUser || {});

    if (email || normalizedUser?.email) {
      setStoredUserEmail(email || normalizedUser.email);
    }

    setInMemoryAccessToken(token || "");
    setUser(normalizedUser || null);
  }, []);

  const hydrateAuth = useCallback(async () => {
    try {
      setIsHydratingAuth(true);

      const storedEmail = getStoredUserEmail();

      if (!storedEmail) {
        clearAuthenticatedSession();
        return null;
      }

      const response = await getAuthStatusByEmail(storedEmail);
      const data = response?.data || {};

      const exists = Boolean(data?.exists);
      const loggedIn = Boolean(data?.logged_in);
      const apiUser = data?.user || null;
      const token = data?.token || "";

      if (!exists || !loggedIn || !apiUser || !token) {
        clearAuthenticatedSession();
        return null;
      }

      const normalizedUser = normalizeAuthUser(apiUser);

      setStoredUserEmail(normalizedUser?.email || storedEmail);
      setInMemoryAccessToken(token);
      setUser(normalizedUser);

      return normalizedUser;
    } catch (error) {
      clearAuthenticatedSession();
      return null;
    } finally {
      setIsHydratingAuth(false);
    }
  }, [clearAuthenticatedSession]);

  useEffect(() => {
    hydrateAuth();
  }, [hydrateAuth]);

  const value = useMemo(
    () => ({
      user,
      setUser,
      isHydratingAuth,
      isAuthenticated: Boolean(user && getInMemoryAccessToken()),
      hydrateAuth,
      setAuthenticatedSession,
      clearAuthenticatedSession,
    }),
    [
      user,
      isHydratingAuth,
      hydrateAuth,
      setAuthenticatedSession,
      clearAuthenticatedSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
